import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Package, Search, TrendingUp, Calendar, DollarSign, 
  ChevronDown, ChevronRight, ShoppingCart, Building2,
  ArrowUpDown, ArrowUp, ArrowDown, Layers, Download
} from 'lucide-react';
import * as XLSX from 'xlsx';

const API = process.env.REACT_APP_BACKEND_URL;

const PurchasingStock = ({ readOnly = false }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'product_name', direction: 'asc' });

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`,
    'Content-Type': 'application/json'
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/purchasing/stock-summary`, { 
        headers: getAuthHeaders() 
      });
      setProducts(response.data);
    } catch (err) {
      console.error('Stok verileri yüklenemedi:', err);
      toast.error('Stok verileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle row expansion
  const toggleRowExpansion = (productName) => {
    setExpandedRows(prev => ({
      ...prev,
      [productName]: !prev[productName]
    }));
  };

  // Sort handling
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'product_name':
          aValue = (a.product_name || '').toLowerCase();
          bValue = (b.product_name || '').toLowerCase();
          break;
        case 'total_quantity':
          aValue = a.total_quantity || 0;
          bValue = b.total_quantity || 0;
          break;
        case 'used_quantity':
          aValue = a.used_quantity || 0;
          bValue = b.used_quantity || 0;
          break;
        case 'remaining_quantity':
          aValue = a.remaining_quantity || 0;
          bValue = b.remaining_quantity || 0;
          break;
        case 'purchase_count':
          aValue = a.purchase_count || 0;
          bValue = b.purchase_count || 0;
          break;
        case 'last_purchase_date':
          aValue = new Date(a.last_purchase_date || 0).getTime();
          bValue = new Date(b.last_purchase_date || 0).getTime();
          break;
        case 'avg_price':
          aValue = a.avg_price || 0;
          bValue = b.avg_price || 0;
          break;
        case 'last_price':
          aValue = a.last_price || 0;
          bValue = b.last_price || 0;
          break;
        case 'total_spent':
          aValue = a.total_spent || 0;
          bValue = b.total_spent || 0;
          break;
        default:
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Sort icon component
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-3 w-3 ml-1 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1 text-orange-600" />
      : <ArrowDown className="h-3 w-3 ml-1 text-orange-600" />;
  };

  // Filter data by search term
  const filteredData = products.filter(product => 
    product.product_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedData = sortData(filteredData);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('tr-TR') + ' ' + date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === 0 || value === null || value === undefined) return '-';
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: 'TRY',
      minimumFractionDigits: 2 
    }).format(value);
  };

  // Excel Export
  const exportToExcel = () => {
    const data = [];
    
    sortedData.forEach(product => {
      // Ürün Ana Satırı
      data.push({
        'Seviye': 'ÜRÜN',
        'Ürün Adı': product.product_name || '-',
        'Model': product.model || '-',
        'Birim': product.unit || '-',
        'Toplam Miktar': product.total_quantity || 0,
        'Alım Sayısı': product.purchase_count || 0,
        'Ort. Fiyat (₺)': product.avg_price || 0,
        'Son Fiyat (₺)': product.last_price || 0,
        'Toplam Tutar (₺)': product.total_spent || 0,
        'Son Alım': product.last_purchase_date ? formatDate(product.last_purchase_date) : '-'
      });
      
      // Alım Detayları
      if (product.purchases && product.purchases.length > 0) {
        product.purchases.forEach((purchase, idx) => {
          data.push({
            'Seviye': `  └ Alım ${idx + 1}`,
            'Ürün Adı': '',
            'Model': purchase.date ? formatDateTime(purchase.date) : '-',
            'Birim': purchase.branch_name || '-',
            'Toplam Miktar': purchase.quantity || 0,
            'Alım Sayısı': '',
            'Ort. Fiyat (₺)': '',
            'Son Fiyat (₺)': purchase.unit_price || 0,
            'Toplam Tutar (₺)': purchase.total || 0,
            'Son Alım': purchase.supplier || '-'
          });
        });
      }
      
      // Boş satır
      data.push({
        'Seviye': '',
        'Ürün Adı': '',
        'Model': '',
        'Birim': '',
        'Toplam Miktar': '',
        'Alım Sayısı': '',
        'Ort. Fiyat (₺)': '',
        'Son Fiyat (₺)': '',
        'Toplam Tutar (₺)': '',
        'Son Alım': ''
      });
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stok Özeti');
    
    ws['!cols'] = [
      { wch: 15 }, { wch: 25 }, { wch: 18 }, { wch: 12 }, 
      { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, 
      { wch: 16 }, { wch: 15 }
    ];

    XLSX.writeFile(wb, `Stok_Ozeti_Detayli_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Stok özeti Excel olarak indirildi');
  };

  // Calculate stats
  const totalProducts = products.length;
  const totalPurchases = products.reduce((sum, p) => sum + (p.purchase_count || 0), 0);
  const totalSpent = products.reduce((sum, p) => sum + (p.total_spent || 0), 0);
  
  // Toplam Stoğa Alınan - Tüm alınan ürünlerin toplam değeri (total_spent ile aynı)
  const totalStockValue = products.reduce((sum, p) => sum + (p.total_spent || 0), 0);
  
  // Kullanılan stok değeri - used_quantity * avg_price
  const usedStockValue = products.reduce((sum, p) => {
    const usedQty = p.used_quantity || 0;
    const avgPrice = p.avg_price || 0;
    return sum + (usedQty * avgPrice);
  }, 0);
  
  // Toplam Stokta Kalan Değeri - remaining_quantity * avg_price
  const remainingStockValue = products.reduce((sum, p) => {
    const remainingQty = p.remaining_quantity || 0;
    const avgPrice = p.avg_price || 0;
    return sum + (remainingQty * avgPrice);
  }, 0);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="purchasing-stock-loading">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Stok Özeti</h1>
            <p className="text-gray-500">Alınan ürünlerin özet listesi</p>
          </div>
        </div>
        <Card>
          <CardContent className="py-6">
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="purchasing-stock">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stok Özeti</h1>
          <p className="text-gray-500">Alınan ürünlerin detaylı listesi ve fiyat geçmişi</p>
        </div>
        <Button 
          variant="outline" 
          onClick={exportToExcel} 
          className="gap-2"
          disabled={sortedData.length === 0}
          data-testid="export-stock-btn"
        >
          <Download className="h-4 w-4" />
          Excel İndir
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Package className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-orange-600 font-medium">Toplam Ürün Çeşidi</p>
                <p className="text-2xl font-bold text-orange-700">{totalProducts}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ShoppingCart className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-blue-600 font-medium">Toplam Alım Sayısı</p>
                <p className="text-2xl font-bold text-blue-700">{totalPurchases}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-purple-600 font-medium">Toplam Stoğa Alınan</p>
                <p className="text-xl font-bold text-purple-700">{formatCurrency(totalStockValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-amber-600 font-medium">Kullanılan Stok Değeri</p>
                <p className="text-xl font-bold text-amber-700">{formatCurrency(usedStockValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Layers className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-emerald-600 font-medium">Stokta Kalan Değeri</p>
                <p className="text-xl font-bold text-emerald-700">{formatCurrency(remainingStockValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Ürün ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="stock-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Product Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-orange-600" />
            Ürün Listesi ({sortedData.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sortedData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Package className="h-12 w-12 mb-4 text-gray-300" />
              <p>Henüz alınmış ürün bulunmuyor</p>
              <p className="text-sm mt-1">Sipariş oluşturduğunuzda burada görünecektir</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('product_name')}
                  >
                    <div className="flex items-center">
                      Ürün Adı
                      <SortIcon columnKey="product_name" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('total_quantity')}
                  >
                    <div className="flex items-center">
                      Toplam Miktar
                      <SortIcon columnKey="total_quantity" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('used_quantity')}
                  >
                    <div className="flex items-center">
                      Kullanılan
                      <SortIcon columnKey="used_quantity" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('remaining_quantity')}
                  >
                    <div className="flex items-center">
                      Kalan
                      <SortIcon columnKey="remaining_quantity" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('purchase_count')}
                  >
                    <div className="flex items-center">
                      Alım Sayısı
                      <SortIcon columnKey="purchase_count" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('avg_price')}
                  >
                    <div className="flex items-center">
                      Ort. Fiyat
                      <SortIcon columnKey="avg_price" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('last_price')}
                  >
                    <div className="flex items-center">
                      Son Fiyat
                      <SortIcon columnKey="last_price" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('total_spent')}
                  >
                    <div className="flex items-center">
                      Toplam Tutar
                      <SortIcon columnKey="total_spent" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('last_purchase_date')}
                  >
                    <div className="flex items-center">
                      Son Alım
                      <SortIcon columnKey="last_purchase_date" />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.map((product) => (
                  <React.Fragment key={product.product_name}>
                    {/* Main Row */}
                    <TableRow 
                      data-testid={`stock-row-${product.product_name}`}
                      className={`cursor-pointer hover:bg-gray-50 ${expandedRows[product.product_name] ? 'bg-orange-50' : ''}`}
                      onClick={() => toggleRowExpansion(product.product_name)}
                    >
                      <TableCell className="w-10">
                        {product.purchase_count > 1 ? (
                          expandedRows[product.product_name] ? (
                            <ChevronDown className="h-4 w-4 text-orange-600" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          )
                        ) : (
                          <Package className="h-4 w-4 text-gray-300" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {product.product_name}
                          {product.purchase_count > 1 && (
                            <Badge variant="outline" className="text-xs">
                              {product.purchase_count} alım
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-blue-600">
                          {product.total_quantity} {product.unit}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-amber-600">
                          {product.used_quantity || 0} {product.unit}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`font-semibold ${product.remaining_quantity > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {product.remaining_quantity || 0} {product.unit}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-orange-100 text-orange-700">
                          {product.purchase_count} kez
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {formatCurrency(product.avg_price)}
                      </TableCell>
                      <TableCell className="font-medium text-emerald-600">
                        {formatCurrency(product.last_price)}
                      </TableCell>
                      <TableCell className="font-semibold text-purple-600">
                        {formatCurrency(product.total_spent)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="h-4 w-4" />
                          {formatDate(product.last_purchase_date)}
                        </div>
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded Purchases Row */}
                    {expandedRows[product.product_name] && product.purchases && product.purchases.length > 0 && (
                      <TableRow className="bg-gray-50">
                        <TableCell colSpan={10} className="p-0">
                          <div className="p-4 border-l-4 border-orange-400">
                            <div className="text-xs font-semibold text-gray-500 mb-3 flex items-center gap-2">
                              <TrendingUp className="h-4 w-4" />
                              ALIM GEÇMİŞİ ({product.purchases.length} kayıt)
                            </div>
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-gray-100">
                                  <TableHead className="text-xs">Tarih</TableHead>
                                  <TableHead className="text-xs">Miktar</TableHead>
                                  <TableHead className="text-xs">Birim Fiyat</TableHead>
                                  <TableHead className="text-xs">Toplam</TableHead>
                                  <TableHead className="text-xs">Tedarikçi</TableHead>
                                  <TableHead className="text-xs">Şube</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {product.purchases.map((purchase, idx) => (
                                  <TableRow key={idx} className="hover:bg-gray-100">
                                    <TableCell className="text-sm">
                                      <div className="flex items-center gap-1">
                                        <Calendar className="h-3 w-3 text-gray-400" />
                                        {formatDateTime(purchase.date)}
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-sm font-medium text-blue-600">
                                      {purchase.quantity} {purchase.unit}
                                    </TableCell>
                                    <TableCell className="text-sm text-emerald-600 font-medium">
                                      {formatCurrency(purchase.price)}
                                    </TableCell>
                                    <TableCell className="text-sm font-semibold text-purple-600">
                                      {formatCurrency(purchase.total)}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                      <div className="flex items-center gap-1">
                                        <ShoppingCart className="h-3 w-3 text-gray-400" />
                                        {purchase.supplier || '-'}
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-sm">
                                      <div className="flex items-center gap-1">
                                        <Building2 className="h-3 w-3 text-gray-400" />
                                        {purchase.branch || '-'}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PurchasingStock;
